import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PublicSiteLayout from "@/components/landing/PublicSiteLayout";

const sectionOrder = [
  "whoWeAre",
  "informationWeCollect",
  "howWeUseInformation",
  "smsCommunications",
  "approvalLinks",
  "legalBases",
  "sharing",
  "retention",
  "security",
  "cookies",
  "rights",
  "internationalTransfers",
  "children",
  "thirdPartyLinks",
  "changes",
  "contact",
] as const;

type SectionKey = (typeof sectionOrder)[number];

export default function Privacy() {
  const { t } = useTranslation();
  const sections = sectionOrder.map((key) => ({
    key,
    title: t(`privacyPage.sections.${key}.title`),
    paragraphs: t(`privacyPage.sections.${key}.paragraphs`, { returnObjects: true }) as string[],
    bullets: t(`privacyPage.sections.${key}.bullets`, { returnObjects: true }) as string[],
    subSections: t(`privacyPage.sections.${key}.subSections`, { returnObjects: true }) as Array<{
      title: string;
      paragraphs?: string[];
      bullets?: string[];
    }>,
  }));

  return (
    <PublicSiteLayout mainClassName="bg-[linear-gradient(180deg,#F7FAFF_0%,#FFFFFF_18%,#FFFFFF_100%)] py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mx-auto max-w-4xl overflow-hidden rounded-[1.75rem] border border-[#D8E3F4] bg-card shadow-[0_32px_90px_-54px_rgba(12,31,79,0.3)]">
            <div className="border-b border-[#E6EDF8] bg-[linear-gradient(135deg,#0C1F4F_0%,#14306F_100%)] px-6 py-8 lg:px-10">
              <p className="text-sm font-medium text-accent">{t("privacyPage.eyebrow")}</p>
              <h1 className="mt-2 text-3xl font-bold text-white lg:text-4xl">{t("privacyPage.title")}</h1>
              <div className="mt-5 flex flex-col gap-1 text-sm text-white/80">
                <p>{t("privacyPage.effectiveDate")}</p>
                <p>{t("privacyPage.lastUpdated")}</p>
              </div>
            </div>

            <div className="px-6 py-8 lg:px-10 lg:py-10">
              <div className="space-y-4 border-b border-border/70 pb-8 text-sm leading-7 text-muted-foreground">
                <p>{t("privacyPage.intro")}</p>
                <p>
                  {t("privacyPage.questionsPrefix")}{" "}
                  <a className="font-medium text-accent hover:underline" href={`mailto:${t("privacyPage.contactEmail")}`}>
                    {t("privacyPage.contactEmail")}
                  </a>
                  .
                </p>
              </div>

              <div className="space-y-10 pt-8">
                {sections.map((section, index) => (
                  <section key={section.key} className="space-y-4">
                    <h2 className="text-xl font-bold text-primary lg:text-2xl">
                      {index + 1}) {section.title}
                    </h2>

                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-7 text-muted-foreground">
                        {paragraph}
                      </p>
                    ))}

                    {section.key === "smsCommunications" ? (
                      <p className="text-sm leading-7 text-muted-foreground">
                        {t("privacyPage.sections.smsCommunications.linkIntro")}{" "}
                        <Link className="font-medium text-accent hover:underline" to="/sms-consent">
                          {t("privacyPage.sections.smsCommunications.smsConsentLinkLabel")}
                        </Link>
                        {t("privacyPage.sections.smsCommunications.linkMiddle")}{" "}
                        <Link className="font-medium text-accent hover:underline" to="/sms-opt-in-proof">
                          {t("privacyPage.sections.smsCommunications.smsOptInProofLinkLabel")}
                        </Link>{" "}
                        {t("privacyPage.sections.smsCommunications.linkEnd")}{" "}
                        <Link className="font-medium text-accent hover:underline" to="/terms">
                          {t("privacyPage.sections.smsCommunications.termsLinkLabel")}
                        </Link>
                        .
                      </p>
                    ) : null}

                    {section.bullets?.length ? (
                      <ul className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground">
                        {section.bullets.map((bullet) => (
                          <li key={bullet} className="list-disc">
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    {section.subSections?.length
                      ? section.subSections.map((subSection) => (
                          <div key={subSection.title} className="space-y-3 rounded-2xl border border-border/70 bg-[#FAFCFF] px-5 py-4">
                            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-primary/80">
                              {subSection.title}
                            </h3>
                            {subSection.paragraphs?.map((paragraph) => (
                              <p key={paragraph} className="text-sm leading-7 text-muted-foreground">
                                {paragraph}
                              </p>
                            ))}
                            {subSection.bullets?.length ? (
                              <ul className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground">
                                {subSection.bullets.map((bullet) => (
                                  <li key={bullet} className="list-disc">
                                    {bullet}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ))
                      : null}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
    </PublicSiteLayout>
  );
}
